import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { UrlUtilService } from '../url/url.util.service';
import { catchError, finalize, first, map, Observable, switchMap } from 'rxjs';
import { CybermetrieEnum } from '../../models/cybermetrie/cybermetrie.enum';
import { CybermetrieCoordonneesService } from '../cybermetrie/cybermetrie.service';
import { SpinnerService } from '../spinner.service';
import { CoordonneesClient } from 'src/app/api/model/coordonnees-client.model';
import { RequeteModifierCoordonnees } from 'src/app/api/model/requete-modifier-coordonnees.model';
import { ReponseCoordonnees } from 'src/app/api/model/reponse-coordonnees.model';
import { ProblemeRest } from 'src/app/api/model/probleme-rest.model';
import { Environment } from 'src/environments/environment.class';

@Injectable({
  providedIn: 'root',
})
export class ChangementCoordonneeCreditAccesDService {
  private static readonly MSG_ERREUR_GET = 'Erreur lors de la récupération des coordonnées';
  private static readonly MSG_ERREUR_PATCH = 'Erreur lors de la modification des coordonnées';
  private static readonly CYBERMETRIE_MAX_LENGTH = 100;
  constructor(
    private readonly httpClient: HttpClient,
    private readonly urlService: UrlUtilService,
    readonly spinnerService: SpinnerService,
    private readonly cybermetrieCoordonneesService: CybermetrieCoordonneesService,
    private readonly environment: Environment
  ) {}

  obtenirCoordoonees$(): Observable<CoordonneesClient> {
    this.spinnerService.afficher();
    return this.httpClient
      .get<ReponseCoordonnees>(this.urlService.getChangementAdresseAccesDUrl(this.environment.creditEndpoint))
      .pipe(
        first(),
        map((data) => this.toCoordonneeClient(data)),
        catchError((err) => {
          const error = err as Error;
          const problemeRest = error?.cause as ProblemeRest;

          const messageErreur =
            this.createErrorMessageFromRestProblemOrNull(problemeRest) ??
            this.createErrorMessageFromErrorOrDefault(ChangementCoordonneeCreditAccesDService.MSG_ERREUR_GET, error);

          this.cybermetrieCoordonneesService.envoyerEvenementPersonnalise(
            CybermetrieEnum.CATEGORIE_TRANSACTION,
            CybermetrieEnum.ACTION_ERREUR_TECHNIQUE,
            CybermetrieEnum.LABEL_MODIFIER_INFOS,
            0,
            CybermetrieEnum.LABEL_INFOS_TOTAL,
            new Map<string, string>([[CybermetrieEnum.MESSAGE_ERREUR, messageErreur]])
          );
          throw err;
        }),
        finalize(() => this.spinnerService.cacher())
      );
  }

  patchCoordonnee$(
    requeteModifierCoordonnees: RequeteModifierCoordonnees,
    typeChangement: string
  ): Observable<CoordonneesClient> {
    this.spinnerService.afficher();
    return this.httpClient
      .patch(this.urlService.getChangementAdresseAccesDUrl(this.environment.creditEndpoint), requeteModifierCoordonnees)
      .pipe(
        first(),
        map((data) => this.validateReponsePatchOrThrow(data)),
        catchError((err) => {
          const error = err as Error;
          const problemeRest = error?.cause as ProblemeRest;

          const messageErreur =
            this.createErrorMessageFromRestProblemOrNull(problemeRest) ??
            this.createErrorMessageFromErrorOrDefault(ChangementCoordonneeCreditAccesDService.MSG_ERREUR_PATCH, error);

          this.cybermetrieCoordonneesService.envoyerEvenementPersonnalise(
            CybermetrieEnum.CATEGORIE_TRANSACTION,
            CybermetrieEnum.ACTION_ERREUR_TECHNIQUE,
            CybermetrieEnum.LABEL_MODIFIER_INFOS,
            0,
            typeChangement,
            new Map<string, string>([[CybermetrieEnum.MESSAGE_ERREUR, messageErreur]])
          );
          throw err;
        }),
        switchMap(() => this.obtenirCoordoonees$())
      );
  }

  private toCoordonneeClient(reponse: ReponseCoordonnees): CoordonneesClient {
    if (reponse?.message != null) {
      const err = new Error(ChangementCoordonneeCreditAccesDService.MSG_ERREUR_GET, { cause: reponse.message });
      throw err;
    }

    if (reponse?.coordonneesClient == null) {
      throw new Error(ChangementCoordonneeCreditAccesDService.MSG_ERREUR_GET);
    }

    return reponse.coordonneesClient;
  }

  private validateReponsePatchOrThrow(reponse: unknown): void {
    if (reponse == null || !(reponse instanceof Object)) {
      return;
    }

    const message = Object.entries(reponse).find(([key, val]) => key === 'message' && val != null)?.[1] as ProblemeRest;
    if (message != null) {
      const err = new Error(ChangementCoordonneeCreditAccesDService.MSG_ERREUR_PATCH, { cause: message });
      throw err;
    }
  }

  private createErrorMessageFromRestProblemOrNull(restProblem?: ProblemeRest | null): string | null {
    if (restProblem == null) {
      return null;
    }

    const messageErreur = `Code [${restProblem?.errors[0]?.code?.toUpperCase()}] | Status [${restProblem?.status}] |  Message [`;
    const longueurMessageMax =
      ChangementCoordonneeCreditAccesDService.CYBERMETRIE_MAX_LENGTH - (messageErreur + ']').length;

    return messageErreur + `${restProblem?.errors[0]?.message?.substring(0, longueurMessageMax).toLowerCase()}]`;
  }

  private createErrorMessageFromErrorOrDefault(defaultMsg: string, error?: Error | null): string {
    let message = error?.message;
    if (message == null || message === '') {
      message = defaultMsg;
    }

    let status = 'N/A';
    if (error instanceof HttpErrorResponse && error?.status != null) {
      status = error.status.toString();
    }

    const messageErreur = `Code [SPACAAD.999999] | Status [${status}] |  Message [`;
    const longueurMessageMax =
      ChangementCoordonneeCreditAccesDService.CYBERMETRIE_MAX_LENGTH - (messageErreur + ']').length;
    return messageErreur + `${message.substring(0, longueurMessageMax).toLowerCase()}]`;
  }
}
